import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";

// ported from main.tsx — build tag + front error reporter (client only)
if (typeof window !== "undefined") {
  document.documentElement.dataset["buildVersion"] = "2026-08-19-tanstack-migration";
  import("@/lib/errorReporter").then(({ initErrorReporting }) => initErrorReporting());
}

const SAME_AS = [
  "https://g.page/r/CYSnMtQRkg54EBM",
  "https://jiia-studio.fr",
  "https://jiiastudio.fr",
  "https://jiiastudio.com",
  "https://www.instagram.com/charline_archi_jiia_studio/",
  "https://www.linkedin.com/company/jiia-studio/",
];

const JSON_LD = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": "https://jiia-studio.com/#organization",
    name: "JIIA Studio",
    alternateName: "JIIA Studio, Charline Jacob architecte d'intérieur",
    description:
      "Cabinet d'architecture d'intérieur et de design de concept à Grézieu-la-Varenne, Ouest lyonnais. Aménagement, couleur, mobilier sur mesure et images 3D pour particuliers et professionnels à Lyon et en Rhône-Alpes.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Grézieu-la-Varenne",
      postalCode: "69290",
      addressRegion: "Auvergne-Rhône-Alpes",
      addressCountry: "FR",
    },
    areaServed: [
      { "@type": "City", name: "Lyon" },
      { "@type": "AdministrativeArea", name: "Ouest lyonnais" },
      { "@type": "AdministrativeArea", name: "Rhône-Alpes" },
    ],
    priceRange: "€€",
    currenciesAccepted: "EUR",
    knowsAbout: [
      "Architecture d'intérieur",
      "Décoration intérieure",
      "Conseil en couleur et colorimétrie",
      "Mobilier sur mesure",
      "Images 3D et visualisation architecturale",
      "Rénovation d'appartement et de maison",
      "Aménagement de commerces, restaurants et hôtels",
      "Accessibilité PMR des locaux professionnels",
      "Fresques murales peintes à la main",
    ],
    knowsLanguage: "fr-FR",
    founder: {
      "@type": "Person",
      "@id": "https://jiia-studio.com/#charline-jacob",
      name: "Charline Jacob",
      jobTitle: "Architecte d'intérieur et spécialiste couleur",
      worksFor: { "@id": "https://jiia-studio.com/#organization" },
      sameAs: SAME_AS,
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Prestations JIIA Studio",
      itemListElement: [
        {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: "1500",
          priceSpecification: {
            "@type": "PriceSpecification",
            priceCurrency: "EUR",
            minPrice: 1500,
            valueAddedTaxIncluded: true,
          },
          itemOffered: {
            "@type": "Service",
            name: "Architecture d'intérieur",
            description:
              "Projet complet : relevé, plans cotés et techniques, rendus 3D, palette chromatique, mobilier sur mesure, coordination des artisans et suivi de chantier.",
          },
        },
        {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: "300",
          priceSpecification: {
            "@type": "PriceSpecification",
            priceCurrency: "EUR",
            minPrice: 300,
            valueAddedTaxIncluded: true,
          },
          itemOffered: {
            "@type": "Service",
            name: "Décoration et conseil couleurs",
            description:
              "Consultation de 2 h sur 1 à 2 pièces, échantillons testés sur place, palette avec références exactes et compte-rendu écrit.",
          },
        },
        {
          "@type": "Offer",
          priceCurrency: "EUR",
          itemOffered: {
            "@type": "Service",
            name: "Fresques murales",
            description:
              "Fresque dessinée pour le mur concerné et peinte à la main, en intérieur, en vitrine ou en façade.",
          },
        },
        {
          "@type": "Offer",
          priceCurrency: "EUR",
          itemOffered: {
            "@type": "Service",
            name: "Images 3D pour architectes et promoteurs",
            description:
              "Perspectives photoréalistes pour concours, dossiers de promotion et communication visuelle.",
          },
        },
      ],
    },
    url: "https://jiia-studio.com",
    sameAs: SAME_AS,
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://jiia-studio.com/#website",
    name: "JIIA Studio",
    url: "https://jiia-studio.com",
    inLanguage: "fr-FR",
    publisher: { "@id": "https://jiia-studio.com/#organization" },
  },
]);

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Architecte d'intérieur Lyon | JIIA Studio" },
      {
        name: "description",
        content:
          "Architecte d'intérieur & spécialiste couleur. Concept, mobilier sur mesure, identité forte. Les espaces dont on se souvient, à Lyon et Rhône-Alpes.",
      },
      { name: "author", content: "JIIA Studio, Charline Jacob" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://jiia-studio.com/" },
      {
        property: "og:title",
        content: "JIIA Studio · Architecte d'intérieur & Designer de concept à Lyon",
      },
      {
        property: "og:description",
        content:
          "Architecte d'intérieur & spécialiste couleur. Concept, mobilier sur mesure, identité forte. Les espaces dont on se souvient, à Lyon et Rhône-Alpes.",
      },
      { property: "og:image", content: "https://jiia-studio.com/og-jiia-studio.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@JIIAStudio" },
      {
        name: "twitter:title",
        content: "JIIA Studio · Architecte d'intérieur & Designer de concept à Lyon",
      },
      {
        name: "twitter:description",
        content:
          "Architecte d'intérieur & spécialiste couleur. Concept, mobilier sur mesure, identité forte. Les espaces dont on se souvient, à Lyon et Rhône-Alpes.",
      },
      { name: "twitter:image", content: "https://jiia-studio.com/og-jiia-studio.jpg" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon-jiia.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon-jiia.png" },
      { rel: "preconnect", href: "https://hncsixnavgtmohfktoau.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://hncsixnavgtmohfktoau.supabase.co" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/cormorant-garamond-latin-wght-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/outfit-latin-wght-normal.woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [{ type: "application/ld+json", children: JSON_LD }],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="fr" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ScrollToTop />
        <Outlet />
        <CookieConsent />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  console.error(error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <h1 className="font-heading text-2xl mb-2">This page didn't load</h1>
        <p className="text-muted-foreground font-body mb-6">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-body text-sm"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="px-4 py-2 rounded-md border border-border text-foreground font-body text-sm"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}